import { useState } from 'react';

import { Grid, GridItem, Link, Text } from '@chakra-ui/react';
import NextLink from 'next/link';
import { useRouter } from 'next/router';

import { InventoryTitles } from '@/utils/models/inventory-items';

interface TitlesListProps {
  titles: InventoryTitles[];
  isSingleColumn?: boolean;
  basePath?: string;
  withLeftMargin?: boolean;
  visibleCount?: number;
  showToggle?: boolean;
  moreLabel?: string;
  lessLabel?: string;
}

export const TitlesList = ({
  titles,
  isSingleColumn = false,
  basePath,
  withLeftMargin = true,
  visibleCount,
  showToggle = false,
  moreLabel = 'More',
  lessLabel = 'Less',
}: TitlesListProps) => {
  const { pathname } = useRouter();
  const [isExpanded, setIsExpanded] = useState(false);

  const shouldLimit = typeof visibleCount === 'number' && visibleCount > 0 && titles.length > visibleCount;
  const displayed = shouldLimit && !isExpanded ? titles.slice(0, visibleCount) : titles;

  return (
    <>
      <Grid
        as="ul"
        gridTemplateColumns={isSingleColumn ? '1fr' : `repeat(auto-fill, minmax(min(100%, max(250px, 100%/4)), 1fr))`}
        gap={{ base: 4 }}
        listStyleType="none"
        mt={{ base: 8, sm: 0 }}
        ml={withLeftMargin ? { sm: 14 } : undefined}
        w="100%"
      >
        {displayed.map(({ title, slug }) => {
          const href = `${basePath ?? pathname}/${slug}`;
          return (
            <GridItem as="li" key={title}>
              <Link as={NextLink} href={href}>
                <Text
                  as="span"
                  variant={{
                    base: 'text-md/lineHeight-6/font-medium',
                  }}
                  color="text-dark.300"
                  _hover={{
                    color: 'violet.500',
                  }}
                  textTransform="capitalize"
                >
                  {title}
                </Text>
              </Link>
            </GridItem>
          );
        })}
      </Grid>
      {showToggle && shouldLimit ? (
        <Text
          as="button"
          type="button"
          onClick={() => setIsExpanded((v) => !v)}
          mt={3}
          color="text-dark.500"
          _hover={{ color: 'violet.600' }}
        >
          {isExpanded ? lessLabel : moreLabel}
        </Text>
      ) : null}
    </>
  );
};
